(function () {
  // Exposes window.RewardsBalance, shared with rewards-spend-points.js (the
  // redeem carousels). Guarded so that if this file is loaded more than once
  // on a page (both rewards-hero and rewards-spend-points can be present),
  // the second inclusion doesn't wipe the in-flight fetch cache below.
  if (!window.RewardsBalance) {
    // ?rewards_proxy= URL override and window.REWARDS_PROXY_BASE take
    // priority over any caller-supplied fallback (e.g. a section's own
    // proxy_base setting), so a QA override applies consistently everywhere
    // on the page rather than only to whichever script checks for it.
    const resolveProxyBase = function (fallback) {
      const params = new URLSearchParams(window.location.search);
      const urlOverride = params.get('rewards_proxy');
      if (urlOverride) return urlOverride.replace(/\/$/, '');
      if (window.REWARDS_PROXY_BASE) return window.REWARDS_PROXY_BASE.replace(/\/$/, '');
      return (fallback || '/apps/teacher').replace(/\/$/, '');
    };

    // Memoized per proxy base so the hero/sticky bar and the redeem
    // carousels share a single /rewards/balance round trip instead of each
    // fetching and reconciling it independently (they'd otherwise both fire
    // the request on every page load, and could reflect two different reads
    // if Yotpo's number changes between the calls).
    const pendingByBase = {};
    const fetchLiveBalance = function (proxyBase) {
      const base = proxyBase || resolveProxyBase();
      if (!pendingByBase[base]) {
        pendingByBase[base] = fetch(base + '/rewards/balance', {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
        })
          .then((res) => {
            if (!res.ok) throw new Error('/rewards/balance ' + res.status);
            return res.json();
          })
          .then((data) => (Number.isFinite(data && data.points_balance) ? data.points_balance : null))
          .catch((e) => {
            console.error('[rewards] failed to load live points balance', e);
            return null;
          });
      }
      return pendingByBase[base];
    };

    // Yotpo only deducts a product redemption's points once the order goes
    // through checkout, so until then the balance still counts points already
    // committed to rewards in the cart. The cart is the source of truth: the
    // figure drops when a reward is added, comes back when it is removed and
    // clears once the order (and Yotpo's own deduction) goes through.
    const cartRoot = function () {
      return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    };

    let pendingPromise = null;
    const fetchPendingPoints = function (refresh) {
      if (!pendingPromise || refresh) {
        pendingPromise = fetch(cartRoot() + 'cart.js', {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
        })
          .then((res) => {
            if (!res.ok) throw new Error('/cart.js ' + res.status);
            return res.json();
          })
          // One redemption per line: its token covers a single unit, so the
          // line quantity is not a multiplier.
          .then((cart) => (cart.items || []).reduce((sum, item) => {
            const props = item.properties || {};
            if (!props._yotpo_loyalty_redemption_id) return sum;
            const points = parseInt(props._swell_points_used, 10);
            return sum + (points > 0 ? points : 0);
          }, 0))
          .catch((e) => {
            console.error('[rewards] failed to read pending reward points from the cart', e);
            return 0;
          });
      }
      return pendingPromise;
    };

    // Live Yotpo balance (or the caller's metafield figure when Yotpo is
    // unreachable) minus the points held by rewards in the cart. Null when
    // there is no base figure at all.
    const fetchEffectiveBalance = function (proxyBase, fallback) {
      return Promise.all([fetchLiveBalance(proxyBase), fetchPendingPoints()]).then((results) => {
        const base = Number.isFinite(results[0]) ? results[0] : fallback;
        if (!Number.isFinite(base)) return null;
        return Math.max(0, base - results[1]);
      });
    };

    // Cart changes come from many scripts (redeem buttons, drawer quantity and
    // remove controls, product forms), so they are picked up from the network
    // log rather than by hooking each caller. Only watched once something on
    // the page subscribes.
    const listeners = [];
    const CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
    let refreshTimer = null;

    const notify = function () {
      fetchPendingPoints(true).then(() => {
        listeners.slice().forEach((cb) => {
          try { cb(); } catch (e) { console.error('[rewards] balance listener failed', e); }
        });
      });
    };

    const scheduleRefresh = function () {
      clearTimeout(refreshTimer);
      refreshTimer = setTimeout(notify, 150);
    };

    let watching = false;
    const watchCart = function () {
      if (watching) return;
      watching = true;
      if (typeof PerformanceObserver === 'function') {
        try {
          new PerformanceObserver((list) => {
            const mutated = list.getEntries().some((entry) => {
              const path = entry.name.split('#')[0].replace(/^[a-z]+:\/\/[^/]+/i, '');
              return CART_MUTATION.test(path);
            });
            if (mutated) scheduleRefresh();
          }).observe({ type: 'resource', buffered: false });
        } catch (e) {
          console.error('[rewards] cannot watch cart changes', e);
        }
      }
      // Back/forward cache restores the page as it was, cart included.
      window.addEventListener('pageshow', (e) => { if (e.persisted) scheduleRefresh(); });
    };

    // Called with no arguments whenever the cart may have changed; listeners
    // re-read fetchEffectiveBalance themselves.
    const onBalanceChange = function (cb) {
      listeners.push(cb);
      watchCart();
    };

    window.RewardsBalance = {
      resolveProxyBase: resolveProxyBase,
      fetchLiveBalance: fetchLiveBalance,
      fetchPendingPoints: fetchPendingPoints,
      fetchEffectiveBalance: fetchEffectiveBalance,
      onBalanceChange: onBalanceChange,
    };
  }

  // The Yotpo->Shopify metafield sync (customer.metafields.app--902876--yotpo
  // .loyalty_current_points_balance) can lag behind Yotpo's own balance. When
  // reachable, replace the server-rendered figures with Yotpo's live number,
  // less the points held by rewards in the cart, for this page view only —
  // never written back to the metafield.
  const DRAWER_SELECTOR = '.account-drawer';

  function collect(root, attr, skipDrawer) {
    const list = Array.prototype.slice.call(root.querySelectorAll('[' + attr + ']'));
    if (!skipDrawer) return list;
    return list.filter((el) => !el.closest(DRAWER_SELECTOR));
  }

  // Matches the `| round: 2` Liquid filter used in rewards-hero.liquid: fixed
  // to 2 decimals then trailing zeros dropped (round(2) on 5.10 renders "5.1").
  function roundLikeLiquid(n) {
    return Math.round(n * 100) / 100;
  }

  // Only fetches when there is something on the page to update, so a page whose
  // only figures live in the (closed) account drawer costs no request.
  function applyLiveBalance(root, skipDrawer) {
    const scope = root || document;
    const balanceEls = collect(scope, 'data-live-points-balance', skipDrawer);
    const cashEls = collect(scope, 'data-live-wallet-cash', skipDrawer);
    const productsEls = collect(scope, 'data-live-wallet-products', skipDrawer);
    const shippingEls = collect(scope, 'data-live-wallet-shipping', skipDrawer);

    if (!balanceEls.length && !cashEls.length && !productsEls.length && !shippingEls.length) return;

    // The server-rendered figure is overwritten below, so keep it as the
    // fallback base for when Yotpo can't be reached.
    const first = balanceEls[0];
    if (first && first.dataset.metafieldBalance == null) {
      first.dataset.metafieldBalance = String(parseInt(first.textContent, 10));
    }
    const metafieldBalance = first ? parseInt(first.dataset.metafieldBalance, 10) : NaN;

    const update = function () {
      window.RewardsBalance.fetchEffectiveBalance(undefined, metafieldBalance).then((balance) => {
        if (!Number.isFinite(balance)) return;
        if (first && parseInt(first.textContent, 10) === balance) return;

        balanceEls.forEach((el) => { el.textContent = balance; });
        cashEls.forEach((el) => { el.textContent = roundLikeLiquid(balance * 0.002); });
        productsEls.forEach((el) => { el.textContent = roundLikeLiquid(balance * 0.003); });
        // Liquid's `divided_by` truncates on integer/integer division.
        shippingEls.forEach((el) => { el.textContent = Math.trunc(balance / 9000); });
      });
    };

    update();
    window.RewardsBalance.onBalanceChange(update);
  }

  window.RewardsBalance.applyLiveBalance = applyLiveBalance;

  // The account drawer sits in the global header, so its points figure would
  // otherwise cost a /rewards/balance round trip on every page view. Update
  // everything outside the drawer now; account-drawer.liquid asks for the
  // drawer's own update the first time it is opened. fetchLiveBalance is
  // memoized, so on the rewards page the drawer reuses the hero's request.
  applyLiveBalance(document, true);
})();
